import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import { LANGUAGES } from "@/i18n";
import { useAuthStore } from "@/stores/authStore";
import api from "@/services/api";

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const user = useAuthStore((s) => s.user);
  const currentLanguageCode = (i18n.resolvedLanguage ?? i18n.language).split("-")[0];

  const handleChange = async (langCode: string) => {
    setAnchorEl(null);
    await i18n.changeLanguage(langCode);
    localStorage.setItem("uklid-language", langCode);

    // Persist to DB if logged in
    if (user?.id) {
      api.put(`/api/users/${user.id}`, { language: langCode }).catch(() => {});
    }
  };

  const current = LANGUAGES.find((l) => l.code === currentLanguageCode) ?? LANGUAGES[0];

  return (
    <>
      <Tooltip title={current.label}>
        <IconButton
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          aria-label={current.label}
          sx={{ flexShrink: 0, minWidth: 40, px: 0.75 }}
        >
          <TranslateIcon fontSize="small" />
          <Typography variant="caption" sx={{ ml: 0.5, lineHeight: 1 }}>
            {current.flag}
          </Typography>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { maxWidth: "calc(100vw - 16px)" } }}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code === currentLanguageCode}
            onClick={() => handleChange(lang.code)}
          >
            <ListItemText>
              {lang.flag} {lang.label}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
